import React, { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./store";
import { AppRoutes } from "./routes";
import { fetchMe } from "./store/slices/authSlice";

import { useAppDispatch, useAppSelector } from "./hooks/useAppStore";
import { selectIsAuthenticated } from "./store/slices/authSlice";
import { fetchMyProfile } from "./store/slices/profileSlice";
import { fetchPublicSettings, selectSettings, selectShouldShowAds } from "./store/slices/settingsSlice";
import { MaintenancePage } from "./pages/Maintenance";
import { usePageTracking } from "./hooks/usePageTracking";
import { CookieConsent } from "./components/ui/CookieConsent";

const AppInit: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const settings = useAppSelector(selectSettings);
  const shouldShowAds = useAppSelector(selectShouldShowAds);
  usePageTracking();

  useEffect(() => {
    dispatch(fetchPublicSettings());
    // Auth tokens live in an httpOnly cookie — always ask the backend whether
    // a session exists rather than checking for a client-readable token.
    dispatch(fetchMe());
  }, [dispatch]);

  // Re-fetch settings whenever the tab regains focus so admin changes apply without a reload
  useEffect(() => {
    const onFocus = () => { dispatch(fetchPublicSettings()); };
    window.addEventListener('visibilitychange', onFocus);
    return () => window.removeEventListener('visibilitychange', onFocus);
  }, [dispatch]);

  // Once settings have loaded, flip the AdSense pause flag.
  // Default in index.html is pauseAdRequests=1, so ads stay off until we explicitly allow them.
  const settingsLoaded = settings !== null;
  useEffect(() => {
    if (!settingsLoaded) return;
    (window as any).adsbygoogle = (window as any).adsbygoogle || [];
    (window as any).adsbygoogle.pauseAdRequests = shouldShowAds ? 0 : 1;
  }, [settingsLoaded, shouldShowAds]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchMyProfile());
    }
  }, [isAuthenticated, dispatch]);

  if (settings?.maintenanceMode) {
    return <MaintenancePage message={settings.maintenanceMessage} />;
  }

  return (
    <>
      <AppRoutes />
      <CookieConsent />
    </>
  );
};

const App: React.FC = () => (
  <Provider store={store}>
    <BrowserRouter>
      <AppInit />
    </BrowserRouter>
  </Provider>
);

export default App;
